// Centralized helpers for the marketplace's UI language preference.
// Order of precedence the rest of the app should follow:
//   1. Signed-in user.language (server-side persisted)
//   2. dresez_lang cookie (per-browser persisted)
//   3. navigator.language (browser default)
//   4. 'en' fallback
//
// Phase-1 launch ships 8 locales. Arabic / RTL is deferred to its own
// engineering milestone (Tailwind dir, MUI RTL theme, chatscope flip).

export const LANGUAGE_COOKIE = 'dresez_lang';
const ONE_YEAR_SECONDS = 60 * 60 * 24 * 365;
export const SUPPORTED_LANGUAGES = ['en', 'id', 'es', 'pt-BR', 'hi', 'ur', 'fr', 'de'];

function readCookie(name) {
  if (typeof document === 'undefined') return null;
  const match = document.cookie.match(new RegExp('(?:^|; )' + name + '=([^;]*)'));
  return match ? decodeURIComponent(match[1]) : null;
}

function writeCookie(name, value) {
  if (typeof document === 'undefined') return;
  document.cookie = `${name}=${encodeURIComponent(value)}; Max-Age=${ONE_YEAR_SECONDS}; Path=/; SameSite=Lax`;
}

// Map any user-supplied / browser-supplied language code to one we ship.
// Examples:
//   'en-US' → 'en'        (region stripped to base)
//   'pt-BR' → 'pt-BR'     (exact match)
//   'pt-PT' → 'pt-BR'     (Portuguese-Portugal folded into our pt-BR ship)
//   'pt'    → 'pt-BR'     (bare code folded into our pt-BR ship)
//   'hi-IN' → 'hi'        (region stripped)
//   'it-IT' → null        (Italian not shipped → caller falls back)
export function normalizeLanguage(raw) {
  if (!raw) return null;
  const lower = String(raw).toLowerCase();
  // Portuguese: every pt-* (and bare 'pt') maps to our pt-BR ship.
  if (lower === 'pt' || lower.startsWith('pt-')) {
    return SUPPORTED_LANGUAGES.includes('pt-BR') ? 'pt-BR' : null;
  }
  // Exact match against SUPPORTED (case-insensitive: e.g. 'pt-br' raw).
  const exact = SUPPORTED_LANGUAGES.find((s) => s.toLowerCase() === lower);
  if (exact) return exact;
  // Region-stripped match (e.g. 'en-us' → 'en', 'hi-in' → 'hi').
  const base = lower.split('-')[0];
  const baseMatch = SUPPORTED_LANGUAGES.find((s) => s.toLowerCase() === base);
  if (baseMatch) return baseMatch;
  return null;
}

export function getBrowserDefault() {
  if (typeof navigator === 'undefined') return 'en';
  return normalizeLanguage(navigator.language) || 'en';
}

export function getStoredLanguage() {
  const fromCookie = normalizeLanguage(readCookie(LANGUAGE_COOKIE));
  if (fromCookie) return fromCookie;
  return getBrowserDefault();
}

// Like getStoredLanguage, but never falls back to the browser default — used
// by the first-launch LanguageWelcome modal to distinguish "user has already
// picked" (cookie present) from "we're inferring from navigator.language"
// (cookie absent). Returns null when no cookie has been written yet.
export function getCookieLanguage() {
  return normalizeLanguage(readCookie(LANGUAGE_COOKIE));
}

export function setStoredLanguage(code) {
  const normalized = normalizeLanguage(code);
  if (!normalized) return;
  writeCookie(LANGUAGE_COOKIE, normalized);
  // Mirror to localStorage for any legacy code path that still reads it.
  try {
    window.localStorage.setItem('language', normalized);
  } catch {
    // ignore
  }
}
